<template>
  <div ref="rootRef" class="extra-right-legend">
    <div
      ref="scrollRef"
      class="legend-scroll"
      :class="{ 'fade-top': canScrollUp, 'fade-bottom': canScrollDown }"
      @scroll.passive="updateOverflowState"
    >
      <div v-for="(item, index) in props.items" :key="index" :style="{ color: item.color }" class="legend-item">
        <div class="label" :style="{ fontSize: labelFontSize }">
          {{ item.label }}
        </div>
        <div class="value" :style="{ fontSize: valueFontSize }">
          {{ item.value.toLocaleString() }}
        </div>
      </div>
      <div class="legend-item legend-total">
        <div class="label" :style="{ fontSize: labelFontSize }">{{ t('bi.total') }}</div>
        <div class="value" :style="{ fontSize: valueFontSize }">{{ totalValue.toLocaleString() }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

//libs
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';

interface Items {
  label: string;
  value: number;
  color: string;
}

interface Props {
  items: Items[];
}

const props = defineProps<Props>();

const { t } = useI18n();

const totalValue = computed(() => {
  return (props.items ?? []).reduce((sum, item) => sum + item.value, 0);
});

const rootRef = ref<HTMLElement | null>(null);
const scrollRef = ref<HTMLElement | null>(null);
const { height: rootHeight, width: rootWidth } = useElementSize(rootRef);

// ย่อ font ตามพื้นที่แนวตั้งก่อน เมื่อถึงขนาดต่ำสุดแล้วค่อยยอมให้ scroll
const VALUE_FONT_MAX = 25;
const VALUE_FONT_MIN = 16;
const LABEL_FONT_MAX = 14;
const LABEL_FONT_MIN = 11;
const ROW_HEIGHT_AT_MAX_FONT = 68; // label + value + margin โดยประมาณเมื่อ font เต็มขนาด

const fontScale = computed(() => {
  const rows = (props.items?.length ?? 0) + 1; // +1 = แถว "ทั้งหมด"
  if (!rootHeight.value || !rows) return 1;
  return Math.min(1, rootHeight.value / (rows * ROW_HEIGHT_AT_MAX_FONT));
});

const valueFontSize = computed(() => `${Math.max(VALUE_FONT_MIN, Math.round(VALUE_FONT_MAX * fontScale.value))}px`);
const labelFontSize = computed(() => `${Math.max(LABEL_FONT_MIN, Math.round(LABEL_FONT_MAX * fontScale.value))}px`);

const canScrollUp = ref(false);
const canScrollDown = ref(false);

function updateOverflowState() {
  const el = scrollRef.value;
  if (!el) return;
  canScrollUp.value = el.scrollTop > 2;
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
}

// รวม rootWidth ด้วย เพราะความกว้างเปลี่ยน → การพับบรรทัดของ label เปลี่ยน → overflow เปลี่ยน
watch(
  [rootHeight, rootWidth, () => props.items],
  async () => {
    await nextTick();
    updateOverflowState();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.extra-right-legend {
  display: flex;
  flex-direction: column;
  height: 100%;
  // กราฟต้องไม่ถูกบีบ (ดู .chart-content-slot ใน custom-chart-container.scss) — legend เป็นฝ่ายหดแทนเมื่อพื้นที่ไม่พอ
  flex: 0 1 auto;
  min-width: 40px; // เพดานล่าง กันไม่ให้ legend หายไปทั้งหมด
  max-width: 130px; // เพดานบน ไม่ให้ legend เบียดพื้นที่กราฟ
  overflow: hidden; // กันชั้นสุดท้ายไม่ให้เนื้อหาดันความกว้างเกิน max-width
  padding-left: 5px;

  .legend-scroll {
    flex: 1 1 auto;
    min-width: 0; // จำเป็น: ถ้าไม่ใส่ ตัวเลข/ข้อความยาวที่ไม่มีจุดตัดจะดันความกว้างเกิน max-width ของ parent
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none; // ใช้ fade บอกว่ามีรายการต่อแทน scrollbar

    &::-webkit-scrollbar {
      display: none;
    }

    &.fade-bottom {
      -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
      mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
    }

    &.fade-top {
      -webkit-mask-image: linear-gradient(to bottom, transparent, #000 24px);
      mask-image: linear-gradient(to bottom, transparent, #000 24px);
    }

    &.fade-top.fade-bottom {
      -webkit-mask-image: linear-gradient(to bottom, transparent, #000 24px, #000 calc(100% - 24px), transparent);
      mask-image: linear-gradient(to bottom, transparent, #000 24px, #000 calc(100% - 24px), transparent);
    }
  }

  .legend-item {
    margin-bottom: 4px;
  }

  .label {
    line-height: 1.25;
    white-space: normal;
    overflow-wrap: anywhere; // ข้อความยาวพับบรรทัด ไม่โดนตัดหาย
  }

  .value {
    line-height: 1.2;
    overflow-wrap: anywhere; // ตัวเลขยาวมากให้พับบรรทัดแทนโดนตัดหาย
  }

  .legend-total {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid #e5e9ef; // เส้นคั่นบอกว่าเป็นผลรวม ต่อจากรายการสุดท้ายตามลำดับปกติ
  }
}
</style>
